import { catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';

import { Logger } from '../logging/default-log.service';
import { BaseService } from './base.service';
import { HttpClient } from './http.service';
import { Planthunter } from '../classes/planthunter';
import { UserService } from './user.service';
import { ConfigurationHelper } from '../helper/configuration.helper';
import { Product } from '../classes/product';
import { ActiveOrderService } from './active.order.service';

// Set two constants for the KVK Shop URLs. These are used for the Angular Router part.
export const huntersOverviewUrl = 'hunters';
export const huntersDetailUrl = 'hunters/detail';

/**
 * @author Hillebrand
 */
@Injectable()
export class PlanthunterService extends BaseService {
  constructor(
    private http: HttpClient,
    logger: Logger,
    private userService: UserService,
    private activeOrderService: ActiveOrderService
  ) {
    super(logger);
  }

  getAmountOfNewPlanthunterItems(): Promise<number> {
    if (this.userService.isLoggedIn()) {
      const url = ConfigurationHelper.getWebshopUrl('/rest/planthunter/newhunts');
      return this.http.get<number>(url).toPromise();
    }
    return Promise.resolve(-1);
  }

  getPlanthunters(amountOfProducts?: number): Promise<Planthunter[]> {
    let url = ConfigurationHelper.getWebshopUrl(`/rest/planthunters?language=${this.getLanguageForPlanthunterCalls()}&shipmentDate=${this.getShipmentDate()}`);

    if (amountOfProducts) {
      url = url + `&amountOfProducts=${amountOfProducts}`;
    }

    return this.http
      .get<Planthunter[]>(url)
      .pipe(catchError(this.handleError))
      .toPromise();
  }

  getMorePlanthunterProducts(planthunterId: string): Promise<Product[]> {
    const url = ConfigurationHelper.getWebshopUrl(`/rest/planthunters/${planthunterId}/get-products?language=${this.getLanguageForPlanthunterCalls()}&shipmentDate=${this.getShipmentDate()}`);

    return this.http
      .get<Product[]>(url)
      .pipe(catchError(this.handleError))
      .toPromise();
  }

  getSinglePlanthunter(
    planthunterId: string,
    amountOfProducts?: number
  ): Promise<Planthunter> {
    let url = ConfigurationHelper.getWebshopUrl(`/rest/planthunter/${planthunterId}?language=${this.getLanguageForPlanthunterCalls()}&shipmentDate=${this.getShipmentDate()}`);

    if (amountOfProducts) {
      url = url + `&amountOfProducts=${amountOfProducts}`;
    }

    return this.http
      .get<Planthunter>(url)
      .pipe(catchError(this.handleError))
      .toPromise();
  }

  followPlanthunter(planthunterId: string): Promise<void> {
    const url = ConfigurationHelper.getWebshopUrl(`/rest/planthunter/${planthunterId}/follow?language=${this.getLanguageForPlanthunterCalls()}`);

    return this.http
      .get<void>(url)
      .pipe(catchError(this.handleError))
      .toPromise();
  }

  unfollowPlanthunter(planthunterId: string): Promise<void> {
    const url = ConfigurationHelper.getWebshopUrl(`/rest/planthunter/${planthunterId}/unfollow?language=${this.getLanguageForPlanthunterCalls()}`);

    return this.http
      .get<void>(url)
      .pipe(catchError(this.handleError))
      .toPromise();
  }

  /**
   * @return the language which should be used for the planthunter calls.
   * @see "java code (NoviWebsiteFrontControllerCustomizer) for more information"
   */
  private getLanguageForPlanthunterCalls(): string {
    const language = (<any>window).planthunterWebshopLanguage;

    return language && language.length > 0
      ? language
      : this.userService.getUserDefaultLanguage();
  }

  /**
   * @return the shipmentDate of the selected active order or null
   */
  private getShipmentDate(): string {
    const selectedOrder = this.activeOrderService.getSavedActiveOrder();

    return selectedOrder ? selectedOrder.shipmentDate.toString() : '';
  }
}
